import type { CSSProperties } from "react";
import { box, HandSvg, ln, mark, markFill, on, we, wp, type HandPart } from "./hand-icon";

/*
  プランと払い方に添えるアイコン。料金表の見出し、支払いの計算機の札、申し込みのページの札で同じ形を使う。
  描き方は hand-icon.tsx の「B 手の揺れの太線」。基本は芽、生涯は無限、最短は稲妻、ライトは羽。
  プランの 3 つは黄の代わりにプランの色で塗る (最短は赤、基本は緑、生涯は青。2026-10-12 オーナー「基本エンジニアプランは緑で」)。
  払い方は、初回入金型の月払いがカレンダーに硬貨、カードの分割がクレジットカード、一括が札、2回払いが分かれ道、銀行振込が銀行の建物。
  線の色は親の字の色を継ぐ
*/
const INF = "M12 12c-2-2.6-3.8-3.9-5.8-3.9a3.85 3.85 0 0 0 0 7.7c2 0 3.8-1.2 5.8-3.8zm0 0c2 2.7 3.8 4 5.9 4a3.95 3.95 0 0 0 0-7.9C15.8 8.1 14 9.4 12 12z";
const INF_R = "M12 12c2 2.7 3.8 4 5.9 4a3.95 3.95 0 0 0 0-7.9C15.8 8.1 14 9.4 12 12z";

export const PLAN_ICON_PATHS: Record<string, HandPart[]> = {
  standard: [
    mark(
      wp(
        [
          [13.8, 2.3],
          [4.3, 13.6],
          [11, 13.6],
          [9.7, 21.7],
          [19.7, 10.2],
          [13, 10.2],
        ],
        { r: 1 },
      ),
    ),
  ],
  basic: [
    "M12.1 21.1c-.3-2.7-.2-5.4.2-8.3",
    ln(6.8, 21.2, 17.2, 21.2),
    mark(we(7.6, 9.4, 4.9, 2.6, 28)),
    mark(we(16.4, 9, 4.9, 2.6, -28)),
  ],
  lifetime: [markFill(INF_R), INF],
  light: [mark("M20 4c-7.2-.2-12.4 3.9-13.4 11.6 7.7-.4 12.6-4.8 13.4-11.6z"), ln(3.6, 20.4, 12.6, 11.4)],
};

/*
  プランの色の正本。最短は赤、基本エンジニアは緑、生涯サポートは青。PlanIcon が data-plan-id を付け、塗りの色 --plan-ic をここで決める。
  墨の地の上 (計算機で選んだ札) は同じ色相の明るい版 (-hi) を使う。scope は .hp か .pay。scope の外では PLAN_COLOR の既定の色で塗る
*/
export const planColorCss = (scope: string) => `
${scope}{--plan-standard:#E5484D;--plan-basic:#30A46C;--plan-lifetime:#1F56D6;--plan-standard-hi:#FF7A7E;--plan-basic-hi:#4FD08F;--plan-lifetime-hi:#7BA0F5}
${scope} svg[data-plan-id="standard"]{--plan-ic:var(--plan-standard)}
${scope} svg[data-plan-id="basic"]{--plan-ic:var(--plan-basic)}
${scope} svg[data-plan-id="lifetime"]{--plan-ic:var(--plan-lifetime)}
${scope} .pp-sim-plan[aria-checked="true"] svg[data-plan-id="standard"]{--plan-ic:var(--plan-standard-hi)}
${scope} .pp-sim-plan[aria-checked="true"] svg[data-plan-id="basic"]{--plan-ic:var(--plan-basic-hi)}
${scope} .pp-sim-plan[aria-checked="true"] svg[data-plan-id="lifetime"]{--plan-ic:var(--plan-lifetime-hi)}
`;

const PLAN_COLOR: Record<string, string> = {
  standard: "var(--plan-ic, #E5484D)",
  basic: "var(--plan-ic, #30A46C)",
  lifetime: "var(--plan-ic, #1F56D6)",
};

const CARD: HandPart[] = [mark(box(2.5, 5, 19, 14)), on(ln(3.4, 10, 20.6, 10, 0.4), 2.4), on(ln(6.4, 15, 10.4, 15), 2.4)];

export const PAY_ICON_PATHS: Record<string, HandPart[]> = {
  subscription: [
    wp(
      [
        [11.5, 21],
        [3, 21],
        [3, 4.5],
        [21, 4.5],
        [21, 11.5],
      ],
      { closed: false },
    ),
    ln(3.2, 9.6, 20.8, 9.6),
    "M8 2.3v3.4M16 2.3v3.4",
    mark(we(18, 18, 3.4)),
  ],
  card: CARD,
  installments: CARD,
  // SMS でコードを送る。電話の形に、黄で塗った吹き出し
  sms: [box(6, 2.5, 12, 19, 2.5), ln(10.6, 5.4, 13.4, 5.4), mark(we(12, 11.6, 3.6, 2.6, 0)), "M10.4 14.2l-1 1.9 2.3-1.1", "M12 18.6h.01"],
  // 銀行振込。屋根を黄で塗った銀行の建物 (屋根、柱4本、土台)
  bank: [
    mark(
      wp(
        [
          [2.8, 9.2],
          [12, 3.2],
          [21.2, 9.2],
        ],
        { r: 1 },
      ),
    ),
    ln(6.2, 11.6, 6.2, 17.6),
    ln(10.1, 11.6, 10.1, 17.6),
    ln(13.9, 11.6, 13.9, 17.6),
    ln(17.8, 11.6, 17.8, 17.6),
    ln(3, 20.6, 21, 20.6),
  ],
  lump: [box(2.5, 6, 19, 12, 2), mark(we(12, 12, 2.7)), "M6.3 12h.01M17.7 12h.01"],
  split: [
    wp(
      [
        [12, 21.5],
        [12, 13.6],
        [4, 4],
      ],
      { closed: false, r: 2.5 },
    ),
    ln(15, 9, 20.5, 3.5),
    wp(
      [
        [15.5, 3.2],
        [20.8, 3.2],
        [20.8, 8.5],
      ],
      { closed: false, r: 1 },
    ),
    wp(
      [
        [3.2, 8.5],
        [3.2, 3.2],
        [8.5, 3.2],
      ],
      { closed: false, r: 1 },
    ),
  ],
};

/** パーツの並びを B の描き方の svg にする。NavIcon・PricingIcon もこれを通す */
export function LineIcon({
  paths,
  className,
  size,
  markColor,
  planId,
}: {
  paths: readonly HandPart[] | undefined;
  className: string;
  size: number;
  /** 黄の代わりに塗る色 */
  markColor?: string;
  /** プランのアイコンだけ。planColorCss が色を当てる目印 */
  planId?: string;
}) {
  if (!paths) return null;
  return (
    <HandSvg
      parts={paths}
      className={className}
      size={size}
      style={markColor ? ({ "--ic-mark": markColor } as CSSProperties) : undefined}
      data-plan-id={planId}
    />
  );
}

export function PlanIcon({ planId, className = "pp-pt-ic", size = 20 }: { planId: string; className?: string; size?: number }) {
  return <LineIcon paths={PLAN_ICON_PATHS[planId]} className={className} size={size} markColor={PLAN_COLOR[planId]} planId={planId} />;
}

export function PayMethodIcon({ method, className = "pp-sim-ic", size = 20 }: { method: string; className?: string; size?: number }) {
  return <LineIcon paths={PAY_ICON_PATHS[method]} className={className} size={size} />;
}
